import { useRef, useCallback, useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { motion, useInView } from 'motion/react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useScrollToSection } from '../hooks/useScrollToSection';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from './ui/carousel';

export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const { scrollToSection } = useScrollToSection();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  // Autoplay
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [api]);

  // Track current slide for dots
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on('select', () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const services = [
    {
      title: 'Gestion Aduanera',
      description: 'Soporte documental y operativo para importacion y exportacion.',
      image: 'https://images.unsplash.com/photo-1731847999830-6f71b78d720e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBzdG9yYWdlJTIwZmFjaWxpdHl8ZW58MXx8fHwxNzc0Nzg3NzQxfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      meta: 'Operacion',
    },
    {
      title: 'Soporte 24/7',
      description: 'Equipo disponible para atender incidencias y necesidades operativas.',
      image: 'https://images.unsplash.com/photo-1619070284836-e850273d69ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzfGVufDF8fHx8MTc3NDg4MzUyNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      meta: 'Continuidad',
    },
    {
      title: 'Mantenimiento',
      description: 'Servicios preventivos y correctivos para infraestructura y equipos.',
      image: 'https://images.unsplash.com/photo-1767639592831-d492beab8cd9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFyayUyMGJ1aWxkaW5nc3xlbnwxfHx8fDE3NzQ4OTE4MDN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      meta: 'Infraestructura',
    },
    {
      title: 'Administracion',
      description: 'Gestion de back office y soporte administrativo para la operacion.',
      image: 'https://images.unsplash.com/photo-1771308136615-3cded1ed5cc0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsb2dpc3RpY3MlMjB0cmFuc3BvcnRhdGlvbiUyMHRydWNrc3xlbnwxfHx8fDE3NzQ4OTE4MDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      meta: 'Gestion',
    },
    {
      title: 'Almacenamiento',
      description: 'Espacios de resguardo temporal y soluciones de cross-docking.',
      image: 'https://images.unsplash.com/photo-1731847999830-6f71b78d720e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBzdG9yYWdlJTIwZmFjaWxpdHl8ZW58MXx8fHwxNzc0Nzg3NzQxfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      meta: 'Logistica',
    },
    {
      title: 'Conectividad Global',
      description: 'Acceso a redes y flujos logisticos con alcance internacional.',
      image: 'https://images.unsplash.com/photo-1758304480372-2f5e11323aeb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjBjZW50ZXIlMjBhZXJpYWx8ZW58MXx8fHwxNzc0ODkxODAzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      meta: 'Expansion',
    },
  ];

  return (
    <section id="services" className="py-20 md:py-32 bg-white border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 mb-5 shadow-sm"
          >
            Servicios
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl text-slate-950 mb-5 tracking-tight"
          >
            Servicios integrados para la operacion
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 leading-relaxed"
          >
            Acompanamos la operacion con servicios de soporte que reducen friccion y aportan continuidad.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
        >
          <Carousel setApi={setApi} opts={{ align: 'start', loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {services.map((service, index) => (
                <CarouselItem key={service.title} className="pl-4 md:basis-1/2 lg:basis-1/3" role="group" aria-label={service.title}>
                  <motion.article
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index }}
                    whileHover={{ y: -6 }}
                    className="h-full"
                  >
                    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow h-full">
                      <div className="relative h-52 overflow-hidden">
                        <ImageWithFallback src={service.image} alt={service.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-slate-950/10 to-transparent" />
                        <div className="absolute left-4 top-4 rounded-full border border-white/40 bg-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                          {service.meta}
                        </div>
                      </div>

                      <div className="p-6">
                        <h3 className="text-3xl text-slate-950 mb-3 font-semibold tracking-tight leading-tight">{service.title}</h3>
                        <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>

                        <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                          Conocer mas
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </motion.article>
                </CarouselItem>
              ))}
            </CarouselContent>

            <div className="flex flex-col items-center gap-4 mt-8">
              <div className="flex justify-center gap-4">
                <CarouselPrevious className="relative left-0 translate-x-0 bg-white border-slate-200 text-slate-900 hover:bg-slate-50" />
                <CarouselNext className="relative right-0 translate-x-0 bg-white border-slate-200 text-slate-900 hover:bg-slate-50" />
              </div>
              {/* Pagination dots */}
              <div className="flex gap-2" role="tablist" aria-label="Servicios">
                {services.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-selected={current === index}
                    aria-label={`Servicio ${index + 1}`}
                    onClick={() => api?.scrollTo(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      current === index ? 'w-8 bg-slate-900' : 'w-2 bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </Carousel>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-16"
        >
          <div className="rounded-3xl border border-slate-200 bg-slate-950 px-8 py-8 md:py-10 text-white shadow-sm flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <h3 className="text-2xl md:text-3xl font-semibold mb-3">Necesitas un servicio personalizado?</h3>
              <p className="text-slate-300 leading-relaxed">
                Disenamos una propuesta alineada con tu operacion, tus tiempos y la escala de tu proyecto.
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-slate-950 font-semibold shadow-sm hover:bg-slate-100 transition-colors whitespace-nowrap"
            >
              Solicitar cotizacion
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
